import { useMutation } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { css } from "../../../../../styled-system/css";
import mynaPortalAuth from "../../../../assets/hokkaido/myna-portal-auth.png";
import Header from "../../../../components/header";
import { LoadingIcon } from "../../../../components/loadingIcon";
import {
  ActionButton,
  ButtonGroup,
  HokkaidoMynaApplicationResult,
  PageBody,
  PageContent,
  PageTitle,
  TermsDocument,
} from "../../../../features/hokkaido-myna-application/components";
import {
  type HokkaidoMynaApplicationResultView,
  enableHokkaidoMynaApplicationPreview,
  isHokkaidoMynaApplicationOpen,
} from "../../../../features/hokkaido-myna-application/state";
import { HOKKAIDO_MYNA_APPLICATION_TERMS } from "../../../../features/hokkaido-myna-application/terms";
import { getCurrentTime } from "../../../../libs/date";
import { ERROR_CODES } from "../../../../libs/errors";
import { t } from "../../../../libs/i18n";

export const Route = createFileRoute("/_authed/hkp/apply/")({
  beforeLoad: async ({ context: { tenant } }) => {
    const currentTime = await getCurrentTime();
    if (!isHokkaidoMynaApplicationOpen(tenant.slug, currentTime.date)) {
      throw redirect({ to: "/" });
    }
    enableHokkaidoMynaApplicationPreview();
  },
  component: HokkaidoMynaApplicationPage,
});

const startMeta = {
  errorCode:
    ERROR_CODES.CONNECT_START_HOKKAIDO_R8_HOUSEHOLDER_MYNA_APPLICATION_FAILED,
} as const;

function HokkaidoMynaApplicationPage() {
  const { client } = Route.useRouteContext();
  const navigate = useNavigate();
  const [step, setStep] = useState<"intro" | "terms" | "auth">("intro");
  const [resultView, setResultView] =
    useState<HokkaidoMynaApplicationResultView | null>(null);

  const start = useMutation({
    mutationFn: () => client.startHokkaidoR8HouseholderMynaApplication({}),
    onSuccess: ({ outcome }) => {
      if (outcome.case === "redirectUri") {
        window.location.href = outcome.value;
        return;
      }
      if (outcome.case === "alreadyReceived") {
        setResultView({ type: "alreadyReceived" });
        return;
      }
      setResultView({ type: "serverError" });
    },
    // 失敗はサーバーエラー画面で受ける（Sentry へは meta 経由で送られる）
    onError: () => setResultView({ type: "serverError" }),
    throwOnError: false,
    meta: startMeta,
  });

  const handleGoHome = useCallback(() => {
    navigate({ to: "/" });
  }, [navigate]);

  const handleRetry = useCallback(() => {
    start.reset();
    setResultView(null);
    setStep("intro");
  }, [start]);

  // 規約は長く、読み終えた位置のまま次の画面へ移ると上部が見えないため
  // biome-ignore lint/correctness/useExhaustiveDependencies: step の切り替えごとに先頭へ戻す
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  const handleBack = useCallback(() => {
    if (step === "auth" && !resultView) {
      setStep("terms");
      return;
    }
    if (step === "terms" && !resultView) {
      setStep("intro");
      return;
    }
    handleGoHome();
  }, [step, resultView, handleGoHome]);

  // 遷移先へ移るまでの間に再度押されないよう、成功後もローディングを維持する
  const isStarting =
    start.isPending || start.data?.outcome.case === "redirectUri";

  return (
    <>
      <Header title="" onClickBack={handleBack} />
      {resultView ? (
        <HokkaidoMynaApplicationResult
          view={resultView}
          onGoHome={handleGoHome}
          onRetry={handleRetry}
        />
      ) : step === "intro" ? (
        <PageContent>
          <PageTitle>{t("ハガキが届いていない場合")}</PageTitle>
          <PageBody>
            {t(
              "2026年5月1日時点で北海道にお住まいの世帯主の方は、ハガキがなくてもマイナンバーカードを使ってポイントを受け取れます。\n\nマイナポータル（国のオンライン窓口）で、5月1日時点の住民票の情報（世帯主かどうか・お住まいの市区町村）を確認します。",
            )}
          </PageBody>
          <PageBody>
            {t("ご用意いただくもの：マイナンバーカード、暗証番号（数字4桁）")}
          </PageBody>
          <ButtonGroup>
            <ActionButton
              variant="primary"
              withArrow
              onClick={() => setStep("terms")}
            >
              {t("次へ")}
            </ActionButton>
            <ActionButton variant="tertiary" onClick={handleGoHome}>
              {t("キャンセル")}
            </ActionButton>
          </ButtonGroup>
        </PageContent>
      ) : step === "terms" ? (
        <PageContent>
          <PageTitle>{t("追加規約への同意")}</PageTitle>
          <PageBody>
            {t(
              "マイナポータルで住民票の情報を確認する前に、以下の追加規約・プライバシーポリシーをお読みいただき、同意のうえお進みください。",
            )}
          </PageBody>
          <TermsDocument {...HOKKAIDO_MYNA_APPLICATION_TERMS} />
          <ButtonGroup>
            <ActionButton
              variant="primary"
              withArrow
              onClick={() => setStep("auth")}
            >
              {t("同意して次へ")}
            </ActionButton>
            <ActionButton variant="tertiary" onClick={handleGoHome}>
              {t("キャンセル")}
            </ActionButton>
          </ButtonGroup>
        </PageContent>
      ) : (
        <PageContent>
          <PageTitle align="center">
            {t("マイナポータルで住民票の情報を確認します")}
          </PageTitle>
          <div className={css({ display: "flex", justifyContent: "center" })}>
            <img
              src={mynaPortalAuth}
              alt=""
              className={css({ w: "230px", h: "230px" })}
            />
          </div>
          <PageBody>
            {t(
              "マイナポータルが開きます。マイナンバーカードでログインし、情報の提供に同意してください。",
            )}
          </PageBody>
          <ActionButton
            variant="secondary"
            withArrow={!isStarting}
            disabled={isStarting}
            onClick={() => start.mutate()}
          >
            {isStarting ? <LoadingIcon /> : t("マイナポータルを開く")}
          </ActionButton>
        </PageContent>
      )}
    </>
  );
}
